import api from '../../../lib/api';
import type { ProspectSearchMessage, ProspectSearchSession, SearchParameters } from '../types';
import { getSafeUserMessage, sanitizeProspectSearchSession } from '../utils/safety';

interface TriggerSearchPayload {
  query: string;
  organizationId: string;
  parameters?: SearchParameters;
}

interface SessionsResponse {
  sessions: ProspectSearchSession[];
  total: number;
}

interface MessagesResponse {
  messages: ProspectSearchMessage[];
  total: number;
}

export const prospectSearchApi = {
  triggerSearch: async (payload: TriggerSearchPayload): Promise<{ sessionRef: string }> => {
    const { data } = await api.post('/prospect-search/search', payload);
    return data;
  },

  getSessions: async (
    organizationId: string,
    limit = 20,
    offset = 0,
  ): Promise<SessionsResponse> => {
    const { data } = await api.get('/prospect-search/sessions', {
      params: { organizationId, limit, offset },
    });
    return data;
  },

  getSession: async (
    sessionRef: string,
    organizationId: string,
  ): Promise<ProspectSearchSession> => {
    try {
      const { data } = await api.get(`/prospect-search/sessions/${sessionRef}`, {
        params: { organizationId },
      });
      return sanitizeProspectSearchSession(data);
    } catch (error) {
      throw new Error(getSafeUserMessage(error));
    }
  },

  cancelSearch: async (sessionRef: string, organizationId: string): Promise<void> => {
    try {
      await api.post(`/prospect-search/sessions/${sessionRef}/cancel`, undefined, {
        params: { organizationId },
      });
    } catch (error) {
      throw new Error(getSafeUserMessage(error));
    }
  },

  getMessages: async (
    organizationId: string,
    sessionRef?: string,
    limit = 50,
    offset = 0,
  ): Promise<MessagesResponse> => {
    const { data } = await api.get('/prospect-search/messages', {
      params: { organizationId, sessionRef, limit, offset },
    });
    return data;
  },

  getAgents: async (): Promise<Array<{ type: string; displayName: string; status: string }>> => {
    const { data } = await api.get('/prospect-search/agents');
    return data;
  },
};
